import { useEffect, useMemo, useState } from 'react'

/**
 * The prompt server is the same host that delivered the add-in: Word loads the
 * taskpane from it, so its origin is already known at runtime. A build-time
 * override exists for the Vite dev server, which serves the UI but no API.
 */
export function promptServerBaseUrl(): string {
  const fromEnv =
    typeof import.meta !== 'undefined'
      ? (import.meta as { env?: Record<string, string | undefined> }).env?.VITE_PROMPTS_URL
      : undefined
  if (fromEnv?.trim()) return fromEnv.trim().replace(/\/+$/, '')
  // A browser extension is served from its own chrome-extension:// origin and
  // has no server behind it — without an explicit URL there is nothing to probe.
  if (window.location.protocol.startsWith('chrome-extension')) return ''
  return window.location.origin.replace(/\/+$/, '')
}

/**
 * Is a prompt server reachable at `baseUrl`? A dev server answers /health with
 * its HTML fallback, which fails the JSON check — exactly what we want, since
 * the add-in then keeps its built-in prompts.
 */
export async function detectPromptServer(baseUrl: string): Promise<boolean> {
  if (!baseUrl) return false
  try {
    const res = await fetch(`${baseUrl}/health`, {
      headers: { Accept: 'application/json' },
      signal: AbortSignal.timeout(5000),
    })
    if (!res.ok) return false
    const body = (await res.json()) as { prompts?: boolean }
    return body.prompts === true
  } catch {
    return false
  }
}

export interface PromptServer {
  baseUrl: string
  /** null while probing, then whether the server answered. */
  available: boolean | null
}

/** Probes once on mount; the answer decides server-backed vs built-in prompts. */
export function usePromptServer(): PromptServer {
  const baseUrl = useMemo(() => promptServerBaseUrl(), [])
  const [available, setAvailable] = useState<boolean | null>(null)

  useEffect(() => {
    let alive = true
    void detectPromptServer(baseUrl).then((ok) => {
      if (alive) setAvailable(ok)
    })
    return () => {
      alive = false
    }
  }, [baseUrl])

  return { baseUrl, available }
}
